import { randomUuid } from "@models/utils/uuid";
import { Emitter } from "@core/events/emitter";
import type {
  AnnotationAuthor,
  AnnotationData,
  AnnotationQuat,
  AnnotationReplyData,
  AnnotationVec3,
} from "@models/data/annotationData";
import {
  ANNOTATION_TYPE,
  annotationDataToDto,
  annotationReplyDataToDto,
  mapAnnotationDtoToData,
  quatToMap,
  vec3ToMap,
  type AnnotationReplyDto,
} from "@models/data/annotationDto";
import {
  ANNOTATION_UPDATE_FLAGS,
  annotationFromWire,
  annotationToWire,
  replyFromWire,
} from "@models/data/annotationWire";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { IAuthModel } from "@models/auth/IAuthModel";
import type { IStorageModel } from "@models/storage/IStorageModel";
import type { IAnnotationsModel } from "./IAnnotationsModel";

/**
 * `CloudAnnotationsModel` — Dynamo-backed comments/annotations model.
 *
 * Replaces {@link InMemoryAnnotationsModel}: the same scene-scoped, in-memory
 * index drives the comments panel, but every mutation is now persisted to the
 * same Dynamo rows Unity's `ProjectDataModel` writes (via `IStorageModel`
 * annotation methods), so threads authored in web and Unity stay coherent.
 *
 * Ownership boundary (Campfire_Web_CloudData + the media-tile port):
 *   - Loads once per project (Unity `ProjectDataModel.LoadAnnotationsData`:739,
 *     one range query for all scenes), on `onActiveProjectChanged`. Keeps
 *     `TextAnnotation` + `SketchAnnotation` rows (measurements/collision/
 *     action annotations stay deferred).
 *   - The `IAnnotationsModel` surface is synchronous, so each mutation applies
 *     to the in-memory index and emits `onChanged` immediately (optimistic),
 *     then fires the async storage write; on failure it reverts the local
 *     change, re-emits, and raises `onPersistError` (surfaced as a toast).
 *   - When there is no cloud context (signed out / no project / offline
 *     transport) it degrades to purely in-memory behavior — comments work but
 *     don't persist.
 *
 * Deferred (stay out of this slice): measurements + measurement replies,
 * per-annotation scene-visibility persistence, entity-attached (non-detached)
 * comments, notifications, export, share-links, and the multiuser
 * `create_annotation`/`edit`/`delete` broadcast + reconcile (session `tc` 2/10).
 * Sketch PNG upload/download lives in `SketchModel` — this model owns only the
 * sketch's annotation ROW (create/finish/cancel + the `SketchUploaded` gate).
 */
export class CloudAnnotationsModel implements IAnnotationsModel {
  readonly onChanged = new Emitter<{ sceneId: string }>();
  readonly onPanelOpenChanged = new Emitter<boolean>();
  readonly onVisibilityChanged = new Emitter<boolean>();
  readonly onPersistError = new Emitter<{ message: string }>();
  readonly onAnnotationMoved = new Emitter<{ sceneId: string; annotationId: string }>();
  readonly onEditRequested = new Emitter<{ sceneId: string; annotationId: string }>();
  readonly onContextMenuRequested = new Emitter<{ sceneId: string; annotationId: string }>();

  private readonly m_byScene = new Map<string, AnnotationData[]>();
  private m_isPanelOpen = false;
  private m_areAnnotationsVisible = true;
  /** Bumped each load so a stale in-flight fetch can't clobber a newer one. */
  private m_loadGen = 0;

  constructor(
    private readonly m_project: IProjectModel,
    private readonly m_auth: IAuthModel,
    private readonly m_storage: IStorageModel,
  ) {
    // Load once per project (all scenes), like Unity's LoadAnnotationsData.
    this.m_project.onActiveProjectChanged.on(() => {
      void this.reload();
    });
    // A new scene opens with its comments showing: Unity's
    // `AnnotationsLayerModel.OnActiveSceneChanged` ends on
    // `AreAnnotationsVisible = true`, so Hide Comments lasts one scene.
    this.m_project.onActiveSceneChanged.on(() => {
      this.setAnnotationsVisible(true);
    });
    if (this.m_project.activeProject) void this.reload();
  }

  get isPanelOpen(): boolean {
    return this.m_isPanelOpen;
  }

  get areAnnotationsVisible(): boolean {
    return this.m_areAnnotationsVisible;
  }

  setPanelOpen(open: boolean): void {
    if (this.m_isPanelOpen === open) return;
    this.m_isPanelOpen = open;
    this.onPanelOpenChanged.emit(open);
  }

  setAnnotationsVisible(visible: boolean): void {
    if (this.m_areAnnotationsVisible === visible) return;
    this.m_areAnnotationsVisible = visible;
    this.onVisibilityChanged.emit(visible);
  }

  getAnnotations(sceneId: string): ReadonlyArray<AnnotationData> {
    return this.m_byScene.get(sceneId) ?? [];
  }

  createComment(sceneId: string, text: string, creator: AnnotationAuthor): AnnotationData {
    const now = new Date().toISOString();
    const annotation: AnnotationData = {
      id: randomUuid(),
      sceneId,
      type: "text",
      text,
      creator: this.stampAccount(creator),
      createdAt: now,
      modifiedAt: now,
      isResolved: false,
      isDetached: true,
      replies: [],
    };
    this.ensureScene(sceneId).unshift(annotation);
    this.onChanged.emit({ sceneId });

    const ctx = this.cloudContext();
    if (ctx) {
      void this.persist(
        sceneId,
        () => this.removeLocal(sceneId, annotation.id),
        () =>
          this.m_storage.createAnnotation(
            ctx.customerId,
            ctx.projectId,
            annotationDataToDto(annotation),
          ),
      );
    }
    this.publishAnnotationEvent("create_annotation", annotation);
    return annotation;
  }

  createAnnotationAt(
    sceneId: string,
    text: string,
    creator: AnnotationAuthor,
    attachmentPosition: AnnotationVec3,
    placementPosition: AnnotationVec3,
    placementRotation: AnnotationQuat = [0, 0, 0, 1],
  ): AnnotationData {
    const now = new Date().toISOString();
    const annotation: AnnotationData = {
      id: randomUuid(),
      sceneId,
      type: "text",
      text,
      creator: this.stampAccount(creator),
      createdAt: now,
      modifiedAt: now,
      isResolved: false,
      // Web stores world-space marker geometry with an empty parent (see the
      // AnnotationData deviation note), so it stays "detached" in Unity terms.
      isDetached: true,
      replies: [],
      attachmentPosition,
      placementPosition,
      placementRotation,
    };
    this.ensureScene(sceneId).unshift(annotation);
    this.onChanged.emit({ sceneId });

    const ctx = this.cloudContext();
    if (ctx) {
      void this.persist(
        sceneId,
        () => this.removeLocal(sceneId, annotation.id),
        () =>
          this.m_storage.createAnnotation(
            ctx.customerId,
            ctx.projectId,
            annotationDataToDto(annotation),
          ),
      );
    }
    this.publishAnnotationEvent("create_annotation", annotation);
    return annotation;
  }

  previewPlacement(
    sceneId: string,
    annotationId: string,
    placementPosition: AnnotationVec3,
    placementRotation: AnnotationQuat = [0, 0, 0, 1],
  ): void {
    // Drag preview — in-memory only, no persist; the renderer moves the marker.
    const res = this.applyUpdate(sceneId, annotationId, (a) => ({
      ...a,
      placementPosition,
      placementRotation,
    }));
    if (!res) return;
    this.onAnnotationMoved.emit({ sceneId, annotationId });
  }

  previewAttachment(
    sceneId: string,
    annotationId: string,
    attachmentPosition: AnnotationVec3,
  ): void {
    // Anchor-drag preview — in-memory only; `reanchorAnnotation` persists it.
    const res = this.applyUpdate(sceneId, annotationId, (a) => ({
      ...a,
      attachmentPosition,
    }));
    if (!res) return;
    this.onAnnotationMoved.emit({ sceneId, annotationId });
  }

  commitPlacement(
    sceneId: string,
    annotationId: string,
    placementPosition: AnnotationVec3,
    placementRotation: AnnotationQuat = [0, 0, 0, 1],
  ): void {
    const now = new Date().toISOString();
    const res = this.applyUpdate(sceneId, annotationId, (a) => ({
      ...a,
      placementPosition,
      placementRotation,
      modifiedAt: now,
    }));
    if (!res) return;
    this.onAnnotationMoved.emit({ sceneId, annotationId });

    this.publishAnnotationEvent("edit_annotation", res.next);

    const ctx = this.cloudContext();
    if (ctx) {
      void this.persist(
        sceneId,
        () => this.restore(sceneId, res.prev),
        () =>
          this.m_storage.updateAnnotationPlacement(
            ctx.customerId,
            ctx.projectId,
            annotationId,
            vec3ToMap(placementPosition),
            quatToMap(placementRotation),
          ),
      );
    }
  }

  reanchorAnnotation(
    sceneId: string,
    annotationId: string,
    attachmentPosition: AnnotationVec3,
    placementPosition: AnnotationVec3,
    placementRotation: AnnotationQuat = [0, 0, 0, 1],
  ): void {
    const now = new Date().toISOString();
    const res = this.applyUpdate(sceneId, annotationId, (a) => ({
      ...a,
      attachmentPosition,
      placementPosition,
      placementRotation,
      modifiedAt: now,
    }));
    if (!res) return;
    // A list-only comment has no marker yet; the renderer rebuilds for it.
    this.onAnnotationMoved.emit({ sceneId, annotationId });

    // Peers compare both positions on a flags-0 edit, so the anchor rides it.
    this.publishAnnotationEvent("edit_annotation", res.next);

    const ctx = this.cloudContext();
    if (ctx) {
      void this.persist(
        sceneId,
        () => this.restore(sceneId, res.prev),
        () =>
          this.m_storage.updateAnnotationAnchor(
            ctx.customerId,
            ctx.projectId,
            annotationId,
            vec3ToMap(attachmentPosition),
            vec3ToMap(placementPosition),
            quatToMap(placementRotation),
          ),
      );
    }
  }

  createSketchAnnotation(
    sceneId: string,
    creator: AnnotationAuthor,
    placementPosition: AnnotationVec3,
    placementRotation: AnnotationQuat,
    fov: number,
  ): AnnotationData {
    const now = new Date().toISOString();
    const annotation: AnnotationData = {
      id: randomUuid(),
      sceneId,
      type: "sketch",
      text: "",
      creator: this.stampAccount(creator),
      createdAt: now,
      modifiedAt: now,
      isResolved: false,
      isDetached: true,
      replies: [],
      // Placement pose = camera pose at capture (Unity BeginSketchAnnotation:
      // pos = ray.origin - dir*0.01, rot = camera rotation). Attachment shares
      // the placement point — web sketches are never part-anchored.
      attachmentPosition: placementPosition,
      placementPosition,
      placementRotation,
      sketchUploaded: false,
      fov,
      orthographicSize: 0,
    };
    this.ensureScene(sceneId).unshift(annotation);
    this.onChanged.emit({ sceneId });

    const ctx = this.cloudContext();
    if (ctx) {
      void this.persist(
        sceneId,
        () => this.removeLocal(sceneId, annotation.id),
        async () => {
          await this.m_storage.createAnnotation(
            ctx.customerId,
            ctx.projectId,
            annotationDataToDto(annotation),
          );
          // Unity parity (ProjectDataModel.cs:2858-2871): the create Put has
          // no `SketchUploaded` attribute and readers default an absent one to
          // TRUE, so the create must be chased with an explicit `false` write
          // or Unity peers treat the half-created sketch as valid.
          await this.m_storage.updateAnnotationSketchUploaded(
            ctx.customerId,
            ctx.projectId,
            annotation.id,
            false,
          );
        },
      );
    }
    this.publishAnnotationEvent("create_annotation", annotation);
    return annotation;
  }

  finishSketch(sceneId: string, annotationId: string): void {
    const now = new Date().toISOString();
    const res = this.applyUpdate(sceneId, annotationId, (a) => ({
      ...a,
      sketchUploaded: true,
      modifiedAt: now,
    }));
    if (!res) return;
    this.onChanged.emit({ sceneId });
    this.publishAnnotationEvent("edit_annotation", res.next);

    const ctx = this.cloudContext();
    if (ctx) {
      void this.persist(
        sceneId,
        () => this.restore(sceneId, res.prev),
        () =>
          this.m_storage.updateAnnotationSketchUploaded(
            ctx.customerId,
            ctx.projectId,
            annotationId,
            true,
          ),
      );
    }
  }

  cancelSketch(sceneId: string, annotationId: string): void {
    // Same permanent-delete path as deleteComment; a cancelled new sketch has
    // no replies and its S3 blobs (if a background already uploaded) stay
    // orphaned — Unity never cleans sketch PNGs either.
    this.deleteComment(sceneId, annotationId);
  }

  applySketchUploadedFromCloud(sketchId: string): void {
    for (const [sceneId, arr] of this.m_byScene) {
      const index = arr.findIndex((a) => a.id === sketchId);
      if (index < 0) continue;
      if (arr[index].sketchUploaded !== true) {
        arr[index] = { ...arr[index], sketchUploaded: true };
        this.onChanged.emit({ sceneId });
      }
      return;
    }
  }

  // -- Inbound annotation live events (tc 2 / tc 10) -------------------
  //
  // Apply-only: the sender already wrote Dynamo, so these never persist.
  // Own-`aid` echoes and active-project scoping are filtered upstream in
  // `projectEventReconciliation`; Unity's listener loops own create/delete
  // back deliberately, and both legs here are idempotent.

  applyAnnotationCreatedFromCloud(wireAnnotation: Record<string, unknown>): void {
    const annotation = annotationFromWire(wireAnnotation);
    if (!annotation) return;
    const sceneId = annotation.sceneId;
    if (!sceneId) return;
    const arr = this.ensureScene(sceneId);
    if (arr.some((a) => a.id === annotation.id)) return; // dupe guard
    arr.unshift(annotation);
    this.onChanged.emit({ sceneId });
  }

  applyAnnotationEditedFromCloud(
    flags: number,
    replyId: string,
    wireAnnotation: Record<string, unknown>,
  ): void {
    const incoming = annotationFromWire(wireAnnotation);
    if (!incoming) return;
    const located = this.locate(incoming.id);
    if (!located) {
      // Unity `AnnotationsLayerModel.OnServerEditAnnotation`: an unknown id
      // with a whole-annotation edit heals as a create (late joiner).
      if (flags === ANNOTATION_UPDATE_FLAGS.Annotation) {
        this.applyAnnotationCreatedFromCloud(wireAnnotation);
      }
      return;
    }
    const { sceneId, index, arr } = located;
    const current = arr[index];

    if (flags === ANNOTATION_UPDATE_FLAGS.Annotation) {
      // Full-field merge EXCLUDING replies — Unity's `CopyTo` sends none.
      arr[index] = { ...incoming, replies: current.replies };
      this.onChanged.emit({ sceneId });
      // A placement move rides the same flags-0 edit; tell the renderer.
      const moved =
        !vec3Equal(current.placementPosition, incoming.placementPosition)
        || !vec3Equal(current.attachmentPosition, incoming.attachmentPosition);
      if (moved) this.onAnnotationMoved.emit({ sceneId, annotationId: incoming.id });
      return;
    }

    const wireReply = Array.isArray(wireAnnotation.replies)
      ? replyFromWire(wireAnnotation.replies[0], incoming.id)
      : null;
    const replies = [...current.replies];
    if (flags === ANNOTATION_UPDATE_FLAGS.AddReply) {
      if (!wireReply || replies.some((r) => r.id === wireReply.id)) return;
      replies.push(wireReply);
    } else if (flags === ANNOTATION_UPDATE_FLAGS.DeleteReply) {
      const at = replies.findIndex((r) => r.id === replyId);
      if (at < 0) return;
      replies.splice(at, 1);
    } else if (flags === ANNOTATION_UPDATE_FLAGS.EditReply) {
      if (!wireReply) return;
      const at = replies.findIndex((r) => r.id === (replyId || wireReply.id));
      if (at < 0) {
        replies.push(wireReply);
      } else {
        replies[at] = wireReply;
      }
    } else {
      return;
    }
    arr[index] = { ...current, replies };
    this.onChanged.emit({ sceneId });
  }

  applyAnnotationDeletedFromCloud(annotationId: string): void {
    const located = this.locate(annotationId);
    if (!located) return;
    located.arr.splice(located.index, 1);
    this.onChanged.emit({ sceneId: located.sceneId });
  }

  /** Find an annotation across scenes (wire events carry no reliable sceneId
   *  on the envelope — `sceneId` is always "" upstream). */
  private locate(
    annotationId: string,
  ): { sceneId: string; index: number; arr: AnnotationData[] } | null {
    for (const [sceneId, arr] of this.m_byScene) {
      const index = arr.findIndex((a) => a.id === annotationId);
      if (index >= 0) return { sceneId, index, arr };
    }
    return null;
  }

  /**
   * Outbound annotation event (Unity `SendCreateAnnotationEvent` /
   * `SendEditAnnotationEvent` / `SendDeleteAnnotationEvent`). The envelope's
   * `sceneId` is deliberately "" — Unity's senders never set it; the scene
   * lives inside `a.SceneID`.
   */
  private publishAnnotationEvent(
    type: "create_annotation" | "edit_annotation",
    annotation: AnnotationData,
    flags: number = ANNOTATION_UPDATE_FLAGS.Annotation,
    replyId = "",
    replies: ReadonlyArray<AnnotationReplyData> = [],
  ): void {
    if (!this.m_project.activeProjectId) return;
    this.m_project.publishLiveProjectEvent(type, {
      sceneId: "",
      update: {
        flags,
        rId: replyId,
        a: annotationToWire(annotation, replies),
      },
    });
  }

  private publishAnnotationDeleted(annotationId: string): void {
    if (!this.m_project.activeProjectId) return;
    this.m_project.publishLiveProjectEvent("delete_annotation", {
      sceneId: "",
      annotationId,
    });
  }

  requestTextEdit(sceneId: string, annotationId: string): void {
    this.onEditRequested.emit({ sceneId, annotationId });
  }

  requestContextMenu(sceneId: string, annotationId: string): void {
    this.onContextMenuRequested.emit({ sceneId, annotationId });
  }

  updateComment(sceneId: string, annotationId: string, text: string): void {
    const now = new Date().toISOString();
    const res = this.applyUpdate(sceneId, annotationId, (a) => ({
      ...a,
      text,
      modifiedAt: now,
    }));
    if (!res) return;
    this.onChanged.emit({ sceneId });
    this.publishAnnotationEvent("edit_annotation", res.next);

    const ctx = this.cloudContext();
    if (ctx) {
      void this.persist(
        sceneId,
        () => this.restore(sceneId, res.prev),
        () => this.m_storage.updateAnnotationText(ctx.customerId, ctx.projectId, annotationId, text),
      );
    }
  }

  addReply(
    sceneId: string,
    annotationId: string,
    message: string,
    creator: AnnotationAuthor,
  ): AnnotationReplyData | null {
    const now = new Date().toISOString();
    const reply: AnnotationReplyData = {
      id: randomUuid(),
      parentId: annotationId,
      message,
      creator: this.stampAccount(creator),
      createdAt: now,
      modifiedAt: now,
    };
    const res = this.applyUpdate(sceneId, annotationId, (a) => ({
      ...a,
      modifiedAt: now,
      replies: [...a.replies, reply],
    }));
    if (!res) return null;
    this.onChanged.emit({ sceneId });

    const ctx = this.cloudContext();
    if (ctx) {
      void this.persist(
        sceneId,
        () => this.restore(sceneId, res.prev),
        () =>
          this.m_storage.createAnnotationReply(
            ctx.customerId,
            ctx.projectId,
            annotationId,
            annotationReplyDataToDto(reply),
          ),
      );
    }
    this.publishAnnotationEvent(
      "edit_annotation",
      res.next,
      ANNOTATION_UPDATE_FLAGS.AddReply,
      reply.id,
      [reply],
    );
    return reply;
  }

  updateReply(sceneId: string, annotationId: string, replyId: string, message: string): void {
    const now = new Date().toISOString();
    const res = this.applyUpdate(sceneId, annotationId, (a) => ({
      ...a,
      replies: a.replies.map((reply) =>
        reply.id === replyId ? { ...reply, message, modifiedAt: now } : reply,
      ),
    }));
    if (!res) return;
    this.onChanged.emit({ sceneId });
    const edited = res.next.replies.find((reply) => reply.id === replyId);
    this.publishAnnotationEvent(
      "edit_annotation",
      res.next,
      ANNOTATION_UPDATE_FLAGS.EditReply,
      replyId,
      edited ? [edited] : [],
    );

    const ctx = this.cloudContext();
    if (ctx) {
      void this.persist(
        sceneId,
        () => this.restore(sceneId, res.prev),
        () =>
          this.m_storage.updateAnnotationReplyText(
            ctx.customerId,
            ctx.projectId,
            annotationId,
            replyId,
            message,
          ),
      );
    }
  }

  deleteReply(sceneId: string, annotationId: string, replyId: string): void {
    const res = this.applyUpdate(sceneId, annotationId, (a) => ({
      ...a,
      replies: a.replies.filter((reply) => reply.id !== replyId),
    }));
    if (!res) return;
    this.onChanged.emit({ sceneId });
    this.publishAnnotationEvent(
      "edit_annotation",
      res.next,
      ANNOTATION_UPDATE_FLAGS.DeleteReply,
      replyId,
    );

    const ctx = this.cloudContext();
    if (ctx) {
      void this.persist(
        sceneId,
        () => this.restore(sceneId, res.prev),
        () =>
          this.m_storage.deleteAnnotationReplies(ctx.customerId, ctx.projectId, annotationId, [
            replyId,
          ]),
      );
    }
  }

  setResolved(sceneId: string, annotationId: string, resolved: boolean): void {
    const now = new Date().toISOString();
    const res = this.applyUpdate(sceneId, annotationId, (a) => ({
      ...a,
      isResolved: resolved,
      modifiedAt: now,
    }));
    if (!res) return;
    this.onChanged.emit({ sceneId });
    this.publishAnnotationEvent("edit_annotation", res.next);

    const ctx = this.cloudContext();
    if (ctx) {
      void this.persist(
        sceneId,
        () => this.restore(sceneId, res.prev),
        () =>
          this.m_storage.resolveAnnotation(
            ctx.customerId,
            ctx.projectId,
            annotationId,
            resolved ? 1 : 0,
          ),
      );
    }
  }

  deleteComment(sceneId: string, annotationId: string): void {
    const arr = this.m_byScene.get(sceneId);
    if (!arr) return;
    const index = arr.findIndex((a) => a.id === annotationId);
    if (index < 0) return;
    const prev = arr[index];
    arr.splice(index, 1);
    this.onChanged.emit({ sceneId });

    this.publishAnnotationDeleted(annotationId);

    const ctx = this.cloudContext();
    if (ctx) {
      const replyIds = prev.replies.map((r) => r.id);
      void this.persist(
        sceneId,
        () => this.reinsert(sceneId, prev, index),
        async () => {
          await this.m_storage.deleteAnnotations(ctx.customerId, ctx.projectId, [annotationId]);
          // Unity leaves reply rows orphaned on annotation delete; we sweep
          // them so the row family goes away cleanly.
          if (replyIds.length > 0) {
            await this.m_storage.deleteAnnotationReplies(
              ctx.customerId,
              ctx.projectId,
              annotationId,
              replyIds,
            );
          }
        },
      );
    }
  }

  // -- Load --------------------------------------------------------

  private async reload(): Promise<void> {
    const gen = ++this.m_loadGen;
    this.m_byScene.clear();

    const ctx = this.cloudContext();
    if (!ctx) {
      // No project / offline — clear and refresh the active scene view.
      this.emitChangedFor([]);
      return;
    }

    try {
      const { annotations, replies } = await this.m_storage.getAnnotations(
        ctx.customerId,
        ctx.projectId,
      );
      if (gen !== this.m_loadGen) return; // superseded by a newer load.

      const repliesByParent = new Map<string, AnnotationReplyDto[]>();
      for (const reply of replies) {
        const parentId = reply.AnnotationId ?? "";
        if (!parentId) continue;
        let list = repliesByParent.get(parentId);
        if (!list) {
          list = [];
          repliesByParent.set(parentId, list);
        }
        list.push(reply);
      }

      const scenesTouched = new Set<string>();
      for (const dto of annotations) {
        // Text comments + sketches (absent type ⇒ 0 ⇒ text). Collision /
        // ActionData annotations remain unmodeled.
        const annotationType = Number(dto.AnnotationType ?? 0);
        if (
          annotationType !== ANNOTATION_TYPE.Text &&
          annotationType !== ANNOTATION_TYPE.Sketch
        ) {
          continue;
        }
        const data = mapAnnotationDtoToData(dto, repliesByParent.get(dto.ID) ?? []);
        if (!data.sceneId) continue;
        // An inbound `create_annotation` can land while this fetch is in
        // flight (it has its own dupe guard but bumps no generation), so skip
        // rows already present rather than listing them twice.
        const target = this.ensureScene(data.sceneId);
        if (target.some((a) => a.id === data.id)) continue;
        target.push(data);
        scenesTouched.add(data.sceneId);
      }

      // Newest-first per scene, matching `createComment`'s unshift ordering.
      for (const arr of this.m_byScene.values()) {
        arr.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
      }

      this.emitChangedFor([...scenesTouched]);
    } catch (err) {
      if (gen !== this.m_loadGen) return;
      console.error("[CloudAnnotationsModel] load failed:", err);
      this.onPersistError.emit({
        message: `Couldn't load comments — ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  }

  // -- Internals ---------------------------------------------------

  private cloudContext(): { customerId: string; projectId: string } | null {
    if (!this.m_storage.isOnline) return null;
    const customerId = this.m_auth.currentUser?.customerId;
    const projectId = this.m_project.activeProject?.id;
    if (!customerId || !projectId) return null;
    return { customerId, projectId };
  }

  /**
   * Run an optimistic cloud write; on failure revert the local state, re-emit
   * `onChanged`, and raise `onPersistError`. Fire-and-forget so the sync
   * `IAnnotationsModel` surface stays synchronous.
   */
  private async persist(
    sceneId: string,
    revert: () => void,
    op: () => Promise<void>,
  ): Promise<void> {
    try {
      await op();
    } catch (err) {
      revert();
      this.onChanged.emit({ sceneId });
      console.error("[CloudAnnotationsModel] persist failed:", err);
      this.onPersistError.emit({
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  /** Stamp the current account id onto the author for cross-platform coherence. */
  private stampAccount(creator: AnnotationAuthor): AnnotationAuthor {
    if (creator.naturalAccountId) return creator;
    const accountId = this.m_auth.currentUser?.customerId;
    return accountId ? { ...creator, naturalAccountId: accountId } : creator;
  }

  private ensureScene(sceneId: string): AnnotationData[] {
    const existing = this.m_byScene.get(sceneId);
    if (existing) return existing;
    const created: AnnotationData[] = [];
    this.m_byScene.set(sceneId, created);
    return created;
  }

  /**
   * Replace one annotation in place via a functional update, returning the
   * prior value so the caller can revert on a failed persist.
   */
  private applyUpdate(
    sceneId: string,
    annotationId: string,
    update: (annotation: AnnotationData) => AnnotationData,
  ): { prev: AnnotationData; next: AnnotationData } | null {
    const arr = this.m_byScene.get(sceneId);
    if (!arr) return null;
    const index = arr.findIndex((a) => a.id === annotationId);
    if (index < 0) return null;
    const prev = arr[index];
    const next = update(prev);
    arr[index] = next;
    return { prev, next };
  }

  private restore(sceneId: string, prev: AnnotationData): void {
    const arr = this.m_byScene.get(sceneId);
    if (!arr) return;
    const index = arr.findIndex((a) => a.id === prev.id);
    if (index >= 0) arr[index] = prev;
    else arr.unshift(prev);
  }

  private reinsert(sceneId: string, annotation: AnnotationData, index: number): void {
    const arr = this.ensureScene(sceneId);
    if (arr.some((a) => a.id === annotation.id)) return;
    arr.splice(Math.min(index, arr.length), 0, annotation);
  }

  private removeLocal(sceneId: string, annotationId: string): void {
    const arr = this.m_byScene.get(sceneId);
    if (!arr) return;
    const index = arr.findIndex((a) => a.id === annotationId);
    if (index >= 0) arr.splice(index, 1);
  }

  private emitChangedFor(scenes: ReadonlyArray<string>): void {
    const set = new Set(scenes);
    const active = this.m_project.activeScene?.id;
    if (active) set.add(active);
    for (const sceneId of set) this.onChanged.emit({ sceneId });
  }
}

/** Exact position compare — an inbound flags-0 edit is a MOVE only when the
 *  marker geometry actually changed (drives `onAnnotationMoved`). */
function vec3Equal(
  a: AnnotationVec3 | undefined,
  b: AnnotationVec3 | undefined,
): boolean {
  if (!a || !b) return a === b || (!a && !b);
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}
